/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useQuickList.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing quick list modal for tenants.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../store/auth';
import { buildApiUrl, getApiHeaders, API_CONFIG } from '../../../config/api';
import type { Tenant } from '../types';

export function useQuickList(currentId?: string | null) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const tenantSlug = user?.tenant?.slug || '';

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListTenants, setQuickListTenants] = useState<Tenant[]>([]);
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);
  const quickListLoadingRef = useRef(false);

  const loadQuickList = useCallback(async () => {
    if (quickListLoadingRef.current) return;

    quickListLoadingRef.current = true;
    setIsQuickListLoading(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        sortBy: 'name',
        sortOrder: 'asc'
      });

      const response = await fetch(
        buildApiUrl(`${API_CONFIG.ENDPOINTS.TENANTS}?${params}`),
        {
          method: 'GET',
          headers: getApiHeaders(tenantSlug, user?.id),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      const rawTenants = Array.isArray(result?.data) ? result.data : [];

      // If we got 100 results, try to load more pages
      let allTenants = [...rawTenants];
      if (rawTenants.length === 100 && result?.pagination) {
        const totalPages = result.pagination.pages || Math.ceil((result.pagination.total || 0) / 100);
        for (let page = 2; page <= totalPages && page <= 10; page++) {
          const nextParams = new URLSearchParams({
            page: page.toString(),
            limit: '100',
            sortBy: 'name',
            sortOrder: 'asc'
          });

          const nextResponse = await fetch(
            buildApiUrl(`${API_CONFIG.ENDPOINTS.TENANTS}?${nextParams}`),
            {
              method: 'GET',
              headers: getApiHeaders(tenantSlug, user?.id),
            }
          );

          if (nextResponse.ok) {
            const nextResult = await nextResponse.json();
            if (Array.isArray(nextResult?.data)) {
              allTenants = [...allTenants, ...nextResult.data];
            }
          }
        }
      }

      setQuickListTenants(allTenants);
    } catch (error) {
      setQuickListTenants([]);
    } finally {
      setIsQuickListLoading(false);
      quickListLoadingRef.current = false;
    }
  }, [tenantSlug, user?.id]);

  // Load quick list when modal opens
  useEffect(() => {
    if (showQuickList && quickListTenants.length === 0 && !quickListLoadingRef.current) {
      loadQuickList();
    }
  }, [showQuickList, quickListTenants.length, loadQuickList]);

  // Filter tenants client-side
  const filteredQuickList = useMemo(() => {
    const tenants = quickListTenants.filter(t => t.id != null);
    if (!quickListSearch.trim()) {
      return tenants;
    }
    const searchLower = quickListSearch.toLowerCase();
    return tenants.filter(
      (tenant) =>
        tenant.name?.toLowerCase().includes(searchLower) ||
        tenant.slug?.toLowerCase().includes(searchLower) ||
        tenant.description?.toLowerCase().includes(searchLower) ||
        tenant.adminEmail?.toLowerCase().includes(searchLower)
    );
  }, [quickListTenants, quickListSearch]);

  const handleItemSelect = useCallback((tenant: Tenant) => {
    setShowQuickList(false);
    navigate(`/admin/multi-tenant/tenants/${tenant.id}`);
  }, [navigate]);

  const handleClose = useCallback(() => {
    setShowQuickList(false);
    setQuickListSearch('');
  }, []);

  return {
    showQuickList,
    setShowQuickList,
    quickListSearch,
    setQuickListSearch,
    filteredQuickList,
    isQuickListLoading,
    handleItemSelect,
    handleClose
  };
}

